import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { getDrawerFields, getDocumentsByFolder, Field, getDocumentTypes } from '../api';
import { searchFolders } from '../api/folders';
import { DocumentGrid } from './DocumentGrid';
import './DossierPage.css';
import './SearchPage.css';
import { http } from '../api/http';
import { FolderCrudModal } from './FolderCrudModal';
import { AddDocumentDropzone } from './AddDocumentDropzone';
import { useDocumentActions } from './useDocumentActions';
import { DocumentModals } from './DocumentModals';
import { formatCell, getOperatorsForField } from './seachpage-helpers';
import ValueControl from './ValueControl';
import { FolderGrid } from './FolderGrid';
import { EyeIcon, EyeOffIcon } from './icons';
import { titleFor } from './search-helpers';

type FolderLite = { id: number; name: string };

function groupFolderFields(fields: Field[]) {
  const folderFields = fields.filter(f => f.folderfield);

  // Field.group is treated as a *start-of-group marker*.
  // Once a group is started, subsequent fields belong to it until a new marker is encountered.
  type GroupBlock = { group: string; fields: Field[]; key: string };

  const blocks: GroupBlock[] = [];
  let active: GroupBlock = { group: 'Algemeen', fields: [], key: '__default__' };
  blocks.push(active);

  for (const f of folderFields) {
    const marker = String((f as any)?.group ?? '').trim();
    if (marker) {
      active = { group: marker, fields: [], key: `group_${marker}_${String(f.name ?? '')}` };
      blocks.push(active);
    }
    active.fields.push(f);
  }

  return blocks.filter(b => b.fields.length > 0);
}

function getFolderValue(rawFolder: any, fieldName: string): any {
  const values = rawFolder?.folderValues ?? rawFolder?.FolderValues ?? rawFolder?.FolderValuesJson ?? null;
  if (values && typeof values === 'object') return (values as any)[fieldName];
  return (rawFolder as any)?.[fieldName] ?? (rawFolder as any)?.[fieldName?.charAt(0)?.toUpperCase() + fieldName?.slice(1)];
}

export default function DossierPage({ drawerId, drawerName, metadataOption }: { drawerId: number | null; drawerName: string | null; metadataOption?: 0 | 1 | number }) {
  const [allFields, setAllFields] = useState<Field[]>([]);
  const folderFields = useMemo(() => allFields.filter(f => f.folderfield), [allFields]);
  const folderFieldGroups = useMemo(() => groupFolderFields(allFields), [allFields]);
  const folderSearchOrder = useMemo(() => folderFields.map(f => f.name), [folderFields]);
  const [hiddenFolderFields, setHiddenFolderFields] = useState<string[]>([]);
  const [showFieldSelector, setShowFieldSelector] = useState(false);

  // folder field grouping for search panel
  const folderSearchBlocks = useMemo(() => {
    const blocks: { key: string; label: string; fields: Field[] }[] = [{ key: '__default__', label: '', fields: [] }];
    let active = blocks[0];
    for (const name of folderSearchOrder) {
      const fld = folderFields.find(f => f.name === name);
      if (!fld) continue;
      if (hiddenFolderFields.includes(fld.name)) continue;
      const marker = String((fld as any)?.group ?? '').trim();
      if (marker) {
        const block = { key: `group_${marker}_${name}`, label: marker, fields: [] as Field[] };
        blocks.push(block);
        active = block;
      }
      active.fields.push(fld);
    }
    return blocks.filter(b => b.fields.length > 0);
  }, [folderFields, folderSearchOrder, hiddenFolderFields]);

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<any | null>(null);
  const [loadingFolderDetail, setLoadingFolderDetail] = useState(false);

  // List/detail toggle
  const [view, setView] = useState<'list' | 'detail'>('list');

  // Folder search state
  type Crit = { operator: string; value: string };
  const [criteria, setCriteria] = useState<Record<string, Crit>>({});
  const [folderResults, setFolderResults] = useState<any[]>([]);
  const [folderResultCols, setFolderResultCols] = useState<string[]>([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderContinuation, setFolderContinuation] = useState<string | null>(null);

  const [documents, setDocuments] = useState<any[]>([]);
  const [docContinuationToken, setDocContinuationToken] = useState<string | null>(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [resultColumns, setResultColumns] = useState<string[]>([]);

  const documentOnlyFields = allFields.filter(f => !f.folderfield);

  // Folder CRUD modal state
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState<'create-root' | 'create-child' | 'edit' | 'delete'>('create-root');

  // Document actions/modals (reuse same as other pages)
  const documentActions = useDocumentActions({
    fields: allFields,
    onDocumentUpdate: (docId, updatedDoc) => {
      setDocuments(prev => prev.map(d => Number((d as any).id ?? (d as any).Id) === docId ? { ...d, ...updatedDoc } : d));
    },
    onDocumentDelete: (docId) => {
      setDocuments(prev => prev.filter(d => Number((d as any).id ?? (d as any).Id) !== docId));
    }
  });

  useEffect(() => {
    if (!drawerId) { setAllFields([]); return; }
    getDrawerFields(drawerId).then(setAllFields).catch(() => setAllFields([]));
  }, [drawerId]);

  useEffect(() => {
    if (!drawerId) { setDocumentTypes([]); return; }
    getDocumentTypes().then(setDocumentTypes).catch(() => setDocumentTypes([]));
  }, [drawerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedFolderId) { setSelectedFolder(null); return; }
      setLoadingFolderDetail(true);
      try {
        const folder = await http<any>(`/api/folder/${selectedFolderId}`);
        if (!cancelled) setSelectedFolder(folder);
      } catch {
        if (!cancelled) setSelectedFolder(null);
      } finally {
        if (!cancelled) setLoadingFolderDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedFolderId]);

  // load saved hidden folder fields per drawer
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`dossier_folder_hidden_${drawerId ?? 'none'}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setHiddenFolderFields(parsed.filter((f: string) => folderFields.some(ff => ff.name === f)));
        else setHiddenFolderFields([]);
      } else setHiddenFolderFields([]);
    } catch { setHiddenFolderFields([]); }
  }, [drawerId, folderFields]);

  function buildSearchCriteria(): Record<string, { operator: string; value: string }> {
    const api: Record<string, { operator: string; value: string }> = {};
    Object.keys(criteria).forEach(k => {
      const c = criteria[k];
      if (!c) return;
      api[k.toLowerCase()] = { operator: c.operator, value: c.value ?? '' };
    });
    return api;
  }

  async function performFolderSearch(reset = true) {
    if (!drawerId) return;
    if (reset) {
      setFolderContinuation(null);
    }
    setFolderLoading(true);
    try {
      const apiCriteria = buildSearchCriteria();
      const res = await searchFolders<any>(drawerId, apiCriteria, undefined, 100, reset ? undefined : folderContinuation ?? undefined);
      setFolderResults(prev => reset ? res.items : [...prev, ...res.items]);
      setFolderContinuation(res.continuationToken ?? null);
      if (reset) {
        const cols = folderFields.map(f => f.name);
        setFolderResultCols(cols);
      }
    } catch (e: any) {
      console.error(e);
      setFolderResults(reset ? [] : folderResults);
    } finally {
      setFolderLoading(false);
    }
  }

  function handleFolderFieldChange(field: string, type: 'operator' | 'value', value: string) {
    const key = field.toLowerCase();
    setCriteria(prev => {
      const existing = prev[key] ?? { operator: 'eq', value: '' };
      if (type === 'operator') return { ...prev, [key]: { operator: value, value: existing.value } };
      return { ...prev, [key]: { operator: existing.operator, value } };
    });
  }

  function openCreateRootFolder() {
    if (!drawerId) return;
    setFolderModalMode('create-root');
    setFolderModalOpen(true);
  }

  function openCreateChildFolder() {
    if (!drawerId || !selectedFolderId) return;
    setFolderModalMode('create-child');
    setFolderModalOpen(true);
  }

  function openEditFolder() {
    if (!drawerId || !selectedFolderId) return;
    setFolderModalMode('edit');
    setFolderModalOpen(true);
  }

  function openDeleteFolder() {
    if (!drawerId || !selectedFolderId) return;
    setFolderModalMode('delete');
    setFolderModalOpen(true);
  }

  async function refreshAfterFolderMutation() {
    if (!drawerId) return;
    if (selectedFolderId) {
      try {
        const folder = await http<any>(`/api/folder/${selectedFolderId}`);
        setSelectedFolder(folder);
      } catch {}
    }
    await performFolderSearch(true);
  }

  async function loadDocs(reset: boolean) {
    if (!drawerId || !selectedFolderId) return;
    setLoadingDocs(true);
    setDocError(null);
    try {
      const token = reset ? null : docContinuationToken;
      const res = await getDocumentsByFolder<any>(drawerId, selectedFolderId, '', 100, token);
      const nextDocs = res.items ?? [];

      setDocuments(prev => reset ? nextDocs : [...prev, ...nextDocs]);
      setDocContinuationToken(res.continuationToken ?? null);

      const discovered = new Set<string>();
      nextDocs.forEach((d) => { if (d.documentValues) Object.keys(d.documentValues).forEach((k) => discovered.add(k)); });
      if (reset) {
        const ordered = documentOnlyFields.filter(f => discovered.has(f.name)).map(f => f.name);
        const remaining = Array.from(discovered).filter(k => ordered.indexOf(k) === -1);
        setResultColumns([...ordered, ...remaining]);
      }
    } catch (e: any) {
      setDocError(e?.message ?? 'Failed loading documents');
      if (reset) { setDocuments([]); setDocContinuationToken(null); }
    } finally {
      setLoadingDocs(false);
    }
  }

  useEffect(() => {
    setDocuments([]);
    setDocContinuationToken(null);
    setResultColumns([]);
    if (drawerId && selectedFolderId) loadDocs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerId, selectedFolderId]);

  // initial folder search when drawer changes
  useEffect(() => {
    setSelectedFolderId(null);
    setSelectedFolder(null);
    setView('list');
    if (drawerId) performFolderSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerId]);

  return (
    <div className="dossier-container">
      {view === 'list' && (
        <div className="search-page" style={{ border: 'none', boxShadow: 'none', height: 'calc(90vh / var(--ui-scale,1))' }}>
          <div className="search-main">
            <form className="search-form" onSubmit={e => { e.preventDefault(); performFolderSearch(true); }}>
              <div className="header-row" style={{ marginBottom: 10 }}>
                <h3>Dossiers zoeken</h3>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" className="btn-primary" disabled={!drawerId} onClick={openCreateRootFolder}>+ Dossier</button>
                  <button type="button" className="field-selector-btn" onClick={() => setShowFieldSelector(v => !v)} title={showFieldSelector ? 'Verberg selectie' : 'Toon selectie'}>
                    {showFieldSelector ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {folderSearchBlocks.map(block => (
                <div key={block.key} className={block.label ? 'fields-groupbox' : 'fields-groupbox fields-groupbox-default'}>
                  {block.label && <div className="fields-groupbox-title"><span>{block.label}</span></div>}
                  <div className="fields-grid">
                    {block.fields.map(f => (
                      <React.Fragment key={f.name}>
                        <span className="label-strong">{f.title ?? f.name}</span>
                        <select
                          value={criteria[f.name?.toLowerCase()]?.operator || 'eq'}
                          onChange={e => handleFolderFieldChange(f.name, 'operator', e.target.value)}
                          className="input-default"
                        >
                          {getOperatorsForField(f).map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                        </select>
                        <div className="relative">
                          <ValueControl
                            field={f}
                            name={f.name}
                            drawerId={drawerId}
                            getCrit={(n) => criteria[n.toLowerCase()] as any}
                            handleChange={(name, which, value) => handleFolderFieldChange(name, which as any, value)}
                            handleInputBlur={() => { }}
                            handleInputKeyDown={() => { }}
                            getMRU={() => []}
                          />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}

              {showFieldSelector && (
                <div className="col-settings-modal">
                  <div className="col-settings-backdrop" onClick={() => setShowFieldSelector(false)} style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1990 }} />
                  <div className="col-settings-panel" style={{ position: 'fixed', left: Math.max(8, Math.round((window.innerWidth - 360) / 2)), top: Math.max(8, Math.round((window.innerHeight - 320) / 2)), width: 360, zIndex: 2000, backgroundColor: '#fff', borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.3)' }} onClick={e=> e.stopPropagation()}>
                    <div className="col-settings-header" style={{ padding: '8px 12px', borderBottom: '1px solid #ddd' }}>
                      <strong>Selecteer zoekvelden</strong>
                    </div>
                    <div style={{ maxHeight: 260, overflowY: 'auto', padding: 8 }}>
                      <div className="col-settings-list">
                        {folderFields.map(f => {
                          const hidden = hiddenFolderFields.includes(f.name);
                          return (
                            <div key={f.name} className="col-settings-item" style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                              <label style={{ flex: 1, userSelect: 'none' }}>
                                <input
                                  type="checkbox"
                                  checked={!hidden}
                                  onChange={() => {
                                    setHiddenFolderFields(prev => {
                                      const next = hidden ? prev.filter(p => p !== f.name) : [...prev, f.name];
                                      try { localStorage.setItem(`dossier_folder_hidden_${drawerId ?? 'none'}`, JSON.stringify(next)); } catch {}
                                      return next;
                                    });
                                  }}
                                />
                                <span style={{ marginLeft: 8 }}>{titleFor(f.name, folderFields)}</span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="col-settings-actions" style={{ padding: 8, textAlign: 'right' }}>
                      <button type="button" className="btn" onClick={() => { setHiddenFolderFields([]); try { localStorage.removeItem(`dossier_folder_hidden_${drawerId ?? 'none'}`); } catch {} }}>Reset</button>
                      <button type="button" className="btn btn-primary" style={{ marginLeft: 8 }} onClick={() => setShowFieldSelector(false)}>Sluiten</button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button type="submit" className="button-primary" style={{ flex: 1 }} disabled={!drawerId || folderLoading}>Zoeken</button>
                {folderContinuation && <button type="button" className="btn-load" onClick={() => performFolderSearch(false)} disabled={folderLoading}>Meer laden</button>}
              </div>
            </form>

            <div className="resizer" aria-hidden />

            <div className="results-pane">
              <FolderGrid
                folders={folderResults}
                fields={folderFields}
                resultColumns={folderResultCols}
                loading={folderLoading}
                emptyMessage="Geen dossiers gevonden"
                onSelect={(f) => {
                  const id = Number((f as any).id ?? (f as any).Id);
                  if (!Number.isFinite(id)) return;
                  setSelectedFolderId(id);
                  setView('detail');
                }}
              />
              {folderContinuation && !folderLoading && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="btn-load" onClick={() => performFolderSearch(false)}>Meer laden</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {view === 'detail' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, justifyContent: 'space-between' }}>
            <div>
              <button type="button" className="btn-secondary" onClick={() => { setView('list'); }}>{'← Terug'}</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-secondary" disabled={!drawerId || !selectedFolderId} onClick={openEditFolder}>Bewerken</button>
              <button type="button" className="btn-danger" disabled={!drawerId || !selectedFolderId} onClick={openDeleteFolder}>Wissen</button>
            </div>
          </div>

          <div className="dossier-folder-detail">
            {!selectedFolderId ? (
              <div className="dossier-empty">Selecteer een dossier om details en documenten te zien.</div>
            ) : loadingFolderDetail ? (
              <div className="dossier-empty">Dossier laden...</div>
            ) : !selectedFolder ? (
              <div className="dossier-empty">Dossier niet gevonden.</div>
            ) : (
              <div className="dossier-folder-groups">
                {folderFieldGroups.map(g => (
                  <div key={(g as any).key ?? g.group} className="dossier-group">
                    <div className="dossier-group-title">{g.group}</div>
                    <div className="dossier-group-body">
                      {g.fields.map(f => {
                        const raw = getFolderValue(selectedFolder, f.name);
                        const formatted = formatCell(f, raw);
                        const text = (formatted === null || formatted === undefined) ? '' : String(formatted);
                        return (
                          <div key={f.name} className="dossier-field-row">
                            <div className="dossier-field-label">{f.title ?? f.name}</div>
                            <div className="dossier-field-value">{text}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="dossier-docs">
            <div className="dossier-docs-toolbar">
              <input
                className="input-default"
                value={''}
                onChange={() => { }}
                placeholder="Zoek in documentinhoud..."
                disabled
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AddDocumentDropzone
                  inline
                  drawerId={drawerId}
                  drawerName={drawerName}
                  documentFields={documentOnlyFields}
                  documentTypes={documentTypes as any}
                  defaultFolderId={selectedFolderId}
                  onImported={(doc) => {
                    if (!doc) return;
                    const id = Number((doc as any).id ?? (doc as any).Id);
                    if (!Number.isFinite(id)) return;
                    const docFolderId = Number((doc as any).folder?.id ?? (doc as any).folder?.Id ?? (doc as any).folderId ?? (doc as any).FolderId);
                    if (selectedFolderId && Number.isFinite(docFolderId) && docFolderId !== selectedFolderId) return;
                    setDocuments(prev => {
                      if (prev.some(d => Number((d as any).id ?? (d as any).Id) === id)) return prev;
                      return [doc as any, ...prev];
                    });
                  }}
                />
                {docError && <div className="error-text">{docError}</div>}
              </div>
            </div>

            <DocumentGrid
              documents={documents}
              fields={documentOnlyFields}
              documentTypes={documentTypes}
              resultColumns={resultColumns}
              pinDescriptionFirst
              includeFolderValues={false}
              emptyMessage={selectedFolderId ? 'Geen documenten gevonden.' : 'Selecteer een dossier.'}
              drawerId={drawerId}
              metadataOption={metadataOption}
              storagePrefix={'dossier'}
              onEdit={documentActions.openEdit}
              onAudit={documentActions.openAudit}
              onDelete={async (doc) => await documentActions.deleteDocument(Number((doc as any)?.id ?? (doc as any)?.Id))}
              onAfterDelete={(deletedIds) => {
                setDocuments(prev => prev.filter((d) => !deletedIds.includes(Number((d as any).id ?? (d as any).Id))));
              }}
              onMetadata={documentActions.openMetadata}
            />

            {selectedFolderId && docContinuationToken && (
              <div className="dossier-load-more">
                <button type="button" className="btn-load" disabled={loadingDocs} onClick={() => loadDocs(false)}>
                  {loadingDocs ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <FolderCrudModal
        open={folderModalOpen}
        mode={folderModalMode}
        drawerId={drawerId ?? 0}
        drawerFields={allFields}
        parentFolderId={folderModalMode === 'create-child'
          ? (selectedFolderId ?? null)
          : (folderModalMode === 'create-root'
            ? -1
            : ((selectedFolder as any)?.folderParentId ?? (selectedFolder as any)?.FolderParentId ?? -1))
        }
        folder={selectedFolderId ? { id: selectedFolderId, name: String((selectedFolder as any)?.folderSortingValue ?? (selectedFolder as any)?.FolderSortingValue ?? (selectedFolder as any)?.folderDescription ?? (selectedFolder as any)?.FolderDescription ?? (selectedFolder as any)?.name ?? (selectedFolder as any)?.Name ?? 'Folder') } : null}
        onClose={() => setFolderModalOpen(false)}
        onSaved={async () => {
          setFolderModalOpen(false);
          await refreshAfterFolderMutation();
        }}
      />

      <DocumentModals
        editDoc={documentActions.editDoc}
        editValues={documentActions.editValues}
        savingEdit={documentActions.savingEdit}
        editError={documentActions.editError}
        documentFields={documentOnlyFields}
        documentTypes={documentTypes}
        onEditValuesChange={documentActions.setEditValues}
        onEditCancel={documentActions.cancelEdit}
        onEditSave={documentActions.saveEdit}
        auditDoc={documentActions.auditDoc}
        audits={documentActions.audits}
        loadingAudits={documentActions.loadingAudits}
        auditError={documentActions.auditError}
        onAuditClose={documentActions.closeAudit}
        metadataOpen={documentActions.metadataOpen}
        metadataRows={documentActions.metadataRows}
        onMetadataClose={documentActions.closeMetadata}
      />
    </div>
  );
}
